import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { and, not, or } from 'ramda';
import PropTypes from 'prop-types';
import GranteesForm from './components/GranteesForm/GranteesForm';
import ButtonContainer from '@@viewCommonComponents/forms/ButtonContainer';
import useSimulationCustomerGranteesForm from './hooks/useSimulationCustomerGranteesForm.js';
import CustomerForm from './components/CustomerForm/CustomerForm';
import { Button, Icon, InfoMessage, PageTitle, Tabs, Tooltip, RequiredFieldsNotice } from '@fasstech/middle-design-system';
import { useEffect, useMemo, useRef } from 'react';
import { useEditNavigationStore } from '@@app/stores/editNavigationStore';
import useHandleGrantees from './hooks/useHandleGrantees.js';
import { formatProject } from '@@viewHelpers/format.jsx';
import { PageLayout } from '@@viewCommonComponents/forms/Layouts.jsx';
import { useTabs } from '@@viewCommonComponents/hooks/useTabs.js';
import TabsPagination from '@@viewCommonComponents/TabsPagination.jsx';
import ExtraUnauthorizedModal from '@@viewCommonComponents/displayer/ExtraUnauthorizedModal';
import BeclmBlockModal from '@@viewCommonComponents/displayer/BeclmBlockModal';
import useTelEmailValidation from '@@viewCommonHooks/useTelEmailValidation.js';
import { getGranteesWithErrors } from '@@viewHelpers/granteesErrorMessage';
import GranteesErrorMessage from '@@viewCommonComponents/GranteesErrorMessage';

const SimulationCustomerGrantees = ({
  jobs,
  regimeObligatoire,
  professionalSituations,
  socioProfessionalCategories,
  activities,
  coverageZones,
  getContract: project,
  middleFeatures,
  getMiddleText,
  getMiddleBoolean,
  getMiddleNumber,
  phoneIndicatives,
  internationalPhoneIndicatives
}) => {
  const defaultValues = useMemo(() => formatProject(project), [project]);

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: false,
    defaultValues
  });

  const { watch, control, formState: { errors } } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'grantees',
    keyName: 'uuid'
  });

  const customer = watch('customer');
  const granteesValues = watch('grantees') || [];
  const granteesErrors = errors?.grantees || [];

  const grantees = getMiddleBoolean('BENEFICIAIRE_DONNEES_CONTRAT_EST_NECESSAIRE')
    ? [customer, ...fields]
    : [customer];

  // Tab from navigation
  const initialSelectedTab = useRef(useEditNavigationStore.getState().customerGranteesTab).current;
  useEffect(() => {
    useEditNavigationStore.getState().clearCustomerGranteesTab();
  }, []);

  const { tabs, setTabs, updateVisibility, isButtonDisabled, displayTabsPagination } = useTabs({
    grantees,
    getMiddleText,
    filteredViewMode: !getMiddleBoolean('BENEFICIAIRE_DONNEES_CONTRAT_EST_NECESSAIRE'),
    initialSelectedTab: initialSelectedTab ?? 0
  });

  const granteesWithErrors = getGranteesWithErrors({
    granteesValues,
    granteesErrors,
    getMiddleText,
    tabs
  });

  const {
    onSubmit,
    unauthorizedModalMessage,
    setUnauthorizedModalMessage,
    isUnauthorized,
    isFetchingForm,
    postalCodeFranceMetro,
    postalCodeDROM,
    $onPrevClick,
    isValid,
    displayAdditionalInfo,
    displayActivityInfo,
    beclmBlocked,
    beclmModalTextKey,
    closeBeclmModal,
    noEligibleOffer
  } = useSimulationCustomerGranteesForm({
    jobs,
    professionalSituations,
    form,
    getMiddleBoolean,
    middleFeatures,
    getMiddleText,
    initialBeclmBlocked: Boolean(project?.beclmBlocked)
  });

  const nbMaxChild = getMiddleNumber('BENEFICIAIRE_TYPE_NB_MAX_CHILD');

  const {
    handleRemoveGrantee,
    displayDeletionModal,
    setDisplayDeletionModal,
    deletingGrantee,
    setDeletingGrantee,
    isDisabledAddingChild,
    isDisabledAddingSpouse,
    isDisabledAddBeneficiary,
    handleAddBeneficiary
  } = useHandleGrantees({
    setTabs,
    nbMaxChild: getMiddleText('BENEFICIAIRE_TYPE_CHILD_LABEL')
      ? getMiddleNumber('BENEFICIAIRE_TYPE_NB_MAX_CHILD')
      : 0,
    fields,
    append,
    remove,
    nbMaxSpouse: getMiddleText('BENEFICIAIRE_TYPE_SPOUSE_LABEL')
      ? getMiddleNumber('BENEFICIAIRE_TYPE_NB_MAX_SPOUSE')
      : 0,
    form
  });

  const { telEmailValidationHasError, telEmailValidationErrorMessage } = useTelEmailValidation(
    form,
    getMiddleBoolean,
    true
  );

  return (
    <FormProvider {...form}>
      <form className={'w-full'} onSubmit={form.handleSubmit(onSubmit)}>
        <fieldset
          disabled={beclmBlocked}
          className={beclmBlocked ? 'pointer-events-none select-none opacity-60 border-0 p-0 m-0 min-w-0' : 'contents'}
        >
          <PageLayout>
            <Icon
              name={tabs.selectedTab === 0 ? 'informations_adherent' : 'informations_beneficiaire'}
              size={70}
            />
            <PageTitle>
              {getMiddleText(
                tabs.selectedTab === 0
                  ? 'ADHERENT_GENERAL_VIEW_TITLE'
                  : 'BENEFICIAIRE_LABEL_GENERAL_VIEW_TITLE'
              )}
            </PageTitle>
            <RequiredFieldsNotice />

            <div className={'flex flex-col gap-4'}>
              <TabsPagination
                updateVisibility={updateVisibility}
                isButtonDisabled={isButtonDisabled}
                show={displayTabsPagination}
              >
                <Tabs
                  items={tabs.tabs}
                  selected={tabs.selectedTab}
                  onClick={(index) =>
                    setTabs({
                      selectedTab: index,
                      tabs: tabs.tabs
                    })
                  }
                  isDisplayed={tabs && tabs.tabs.length > 1}
                />
              </TabsPagination>
            </div>
            <CustomerForm
              getMiddleText={getMiddleText}
              getMiddleBoolean={getMiddleBoolean}
              getMiddleNumber={getMiddleNumber}
              postalCodeFranceMetro={postalCodeFranceMetro}
              postalCodeDROM={postalCodeDROM}
              professionalSituations={professionalSituations}
              socioProfessionalCategories={socioProfessionalCategories}
              activities={activities}
              coverageZones={coverageZones}
              regimeObligatoire={regimeObligatoire}
              phoneIndicatives={phoneIndicatives}
              internationalPhoneIndicatives={internationalPhoneIndicatives}
              jobs={jobs}
              selectedTab={tabs.selectedTab}
              displayAdditionalInfo={displayAdditionalInfo}
              displayActivityInfo={displayActivityInfo}
              telEmailValidationHasError={telEmailValidationHasError}
              telEmailValidationErrorMessage={telEmailValidationErrorMessage}
            />

            {getMiddleBoolean('BENEFICIAIRE_DONNEES_CONTRAT_EST_NECESSAIRE') && (
              <GranteesForm
                professionalSituations={professionalSituations}
                regimeObligatoire={regimeObligatoire}
                getMiddleText={getMiddleText}
                getMiddleBoolean={getMiddleBoolean}
                getMiddleNumber={getMiddleNumber}
                selectedTab={tabs.selectedTab}
                fields={fields}
                handleRemoveGrantee={handleRemoveGrantee}
                displayDeletionModal={displayDeletionModal}
                setDisplayDeletionModal={setDisplayDeletionModal}
                deletingGrantee={deletingGrantee}
                setDeletingGrantee={setDeletingGrantee}
                isDisabledAddingChild={isDisabledAddingChild}
                isDisabledAddingSpouse={isDisabledAddingSpouse}
              />
            )}
            {noEligibleOffer && (
              <InfoMessage iconName={'lightbulb'}>
                {'L\'assureur ne prévoit aucune offre correspondant à votre besoin'}
              </InfoMessage>
            )}
          </PageLayout>
        </fieldset>
        <ButtonContainer
          $onPreviousButtonClick={$onPrevClick}
          nextButtonLabel={getMiddleText('GENERAL_COMMON_TEXTS_NEXT_BUTTON_LABEL')}
          nextButtonIsDisabled={
            isFetchingForm || isUnauthorized || not(isValid) || telEmailValidationHasError || beclmBlocked || noEligibleOffer
          }
          nextButtonIsHidden={beclmBlocked}
          displayPreviousButton={!beclmBlocked}
          isFormError={not(isValid) || telEmailValidationHasError}
          getMiddleText={getMiddleText}
          displayResumeButton={!beclmBlocked}
          errorMessage={
            granteesWithErrors.length > 0 ? (
              <GranteesErrorMessage granteesWithErrors={granteesWithErrors} />
            ) : null
          }>
          {getMiddleBoolean('BENEFICIAIRE_DONNEES_CONTRAT_EST_NECESSAIRE') && !beclmBlocked && (
            <div className={'flex gap-4 whitespace-nowrap'}>
              <Tooltip
                isDisplayed={isDisabledAddBeneficiary}
                text={getMiddleText('BENEFICIAIRE_TYPE_MAX_CHILD_ATTEINT', {
                  nbMaxChild: nbMaxChild
                })}>
                <Button
                  variant="secondary"
                  isFormError={and(not(isValid), not(isDisabledAddBeneficiary))}
                  onClick={handleAddBeneficiary}
                  disabled={or(isDisabledAddBeneficiary, isUnauthorized) || !isValid || beclmBlocked}
                  data-testid="BENEFICIAIRE_AJOUT_LIEN"
                  className={'w-full'}>
                  {getMiddleText('BENEFICIAIRE_LABEL_AJOUT_LIEN')}
                </Button>
              </Tooltip>
            </div>
          )}
        </ButtonContainer>
      </form>
      <ExtraUnauthorizedModal
        isDisplayed={unauthorizedModalMessage}
        unauthorizedModalMessage={unauthorizedModalMessage}
        setUnauthorizedModalMessage={setUnauthorizedModalMessage}
      />
      <BeclmBlockModal
        isDisplayed={Boolean(beclmModalTextKey)}
        onClose={closeBeclmModal}
        getMiddleText={getMiddleText}
        textKey={beclmModalTextKey}
      />
    </FormProvider>
  );
};

SimulationCustomerGrantees.propTypes = {
  jobs: PropTypes.array,
  regimeObligatoire: PropTypes.object,
  professionalSituations: PropTypes.array,
  socioProfessionalCategories: PropTypes.array,
  activities: PropTypes.array,
  coverageZones: PropTypes.array,
  getContract: PropTypes.object,
  middleFeatures: PropTypes.object,
  getMiddleText: PropTypes.func,
  getMiddleBoolean: PropTypes.func,
  getMiddleNumber: PropTypes.func
};

export default SimulationCustomerGrantees;
